<template>
  <BaseModal
    is-open
    mobile-full-screen-modal
    :size="modalSize"
    :title="modalTitle"
    stop-scroll-class="overflow-hidden"
    data-test-id="message-host-modal"
    @close="close"
  >
    <div class="md:flex">
      <div
        class="md:mr-8 mb-6 md:mb-4 enquiryFormModalSidebar"
        :class="{
          '-mt-7': isMobile,
        }"
      >
        <RtbRoomPricingBar v-if="$store.state.requestToBookPath && !$store.state.showSuspensionWarning" />
        <EnquiryFormHeader
          v-else
          class="mt-6 mb-5"
          ref="enquiryFormHeader"
        />
        <p v-if="$store.getters.isRtbFlowPossible && registerOrLoginForm" class="mt-4">
          Let the host know who’s enquiring.
        </p>
        <ul v-else-if="$store.getters.isRtbFlowPossible && !$store.state.requestToBookPath" class="mb-6 px-5">
          <li class="my-2">
            Introduce yourself
          </li>
          <li class="my-2">
            Tell the host about your activity
          </li>
          <li class="my-2">
            Let the host know how you’d like to use the space
          </li>
        </ul>
      </div>

      <div v-if="$store.getters.isRtbFlowPossible && registerOrLoginForm" class="w-full">
        <UserInfo
          ref="userInfo"
          class="mt-6 mb-4"
          :is-login-form="registerOrLoginForm === 'login'"
          :is-form-valid="canTriggerOAuth"
          @oAuthSubmit="onSuccessfulLoginOrRegister"
          @click="(element) => track('contact_details', 'click', element)"
        />
        <BaseButton
          color="red"
          size="large"
          class="flex justify-center items-center w-full"
          :is-loading="isLoggingInProgress || isLoading.sendEnquiry"
          @click="onSubmitMessageHost"
        >
          Send message
        </BaseButton>
        <div>
          <BackButton class="text-xs" @click="backButton" />
        </div>

        <LegalNotice
          v-if="registerOrLoginForm === 'register'"
          class="mt-2 -mb-2 text-center leading-4"
          :is-account-created-on-this-view="true"
          @termsAndConditionsClick="track('terms_and_conditions')"
          @privacyPolicyClick="track('privacy_policy')"
          @cancellationPolicyClick="track('cancellation_policy')"
        />

        <div v-if="false" class="mt-8 pt-5 border-0 border-grey-300 border-t border-solid text-center">
          <template v-if="registerOrLoginForm === 'register'">
            Already have a User Account? <a class="font-semibold cursor-pointer" @click="registerOrLoginForm = 'login'">Log in</a>
          </template>
          <template v-else>
            Don’t have a User Account? <a class="font-semibold cursor-pointer" @click="registerOrLoginForm = 'register'">Sign up</a>
          </template>
        </div>
      </div>

      <div v-else class="w-full">
        <div v-if="$store.state.showSuspensionWarning && !$store.state.user.lastEnquiry.date">
          <SuspensionWarning class="mb-8" />
        </div>

        <AlreadyEnquired
          v-else-if="$store.state.user.lastEnquiry.date"
          class="mb-6"
        />

        <HasLeadHubOffer
          v-else-if="$store.state.leadHubOffer"
          class="mb-6"
        />

        <CapacityExceededWarning
          v-else-if="capacityExceededWarningConfig.show"
          :people="capacityExceededWarningConfig.people"
          :is-enquire-btn-loading="isLoading.sendEnquiry"
          @continue="continueAnyway"
          @back="capacityExceededWarningConfig.show = false"
        />

        <div v-else>
          <DateAndPeople
            ref="dateAndPeople"
            class="mb-4"
            :class="{
              'mb-4': !$store.state.requestToBookPath,
              'mb-8': $store.state.requestToBookPath,
            }"
            :modal="true"
            :force-people-field-separate="$store.getters.isRtbFlowPossible && !$store.state.requestToBookPath"
            :title-visible="!$store.getters.isRtbFlowPossible"
            @track="({ element, interaction, value = null }) => track(element, interaction, value)"
            @input="onFormInteraction"
          />

          <RestrictionsInfo
            v-if="shouldShowRestrictions"
            class="mb-4"
            :restrictions="restrictions"
          />

          <MessageAndCatering
            :class="{
              'hidden': $store.state.requestToBookPath,
            }"
            ref="messageAndCatering"
            class="mb-8"
            :modal="true"
            @track="({ element, interaction, value = null }) => track(element, interaction, value)"
          />

          <div v-if="!$store.getters.isRtbFlowPossible || ($store.getters.isRtbFlowPossible && $store.state.requestToBookPath && !$store.state.requestToBookMobilePath)">
            <UserInfo
              v-if="!isLoggedIn"
              ref="userInfo"
              class="mb-4"
              :is-form-valid="canTriggerOAuth"
              @validateEntireForm="validateEntireForm"
              @oAuthSubmit="submit(true)"
              @click="(element) => track('contact_details', 'click', element)"
            />
          </div>

          <Captcha :config="$store.state.captcha" @onTokenResponse="(token) => { $store.commit('setCaptchaToken', token); }" />

          <template v-if="$store.state.requestToBookPath && $store.state.pricing.paymentDetails">
            <div class="mt-8 -mb-2 font-bold">
              Price details
            </div>
            <EnquiryPricingTable class="mb-8" />
          </template>

          <div v-if="$store.getters.isRtbFlowPossible && $store.state.requestToBookPath" style="height: 80px" />
          <div
            class="flex md:flex-row flex-col-reverse justify-end mb-4"
            :class="{
              'absolute bottom-0 w-full left-0 px-6': $store.getters.isRtbFlowPossible && $store.state.requestToBookPath,
            }"
          >
            <BaseButton
              v-if="!$store.getters.isRtbFlowPossible || !isMobileOrTablet"
              color="white"
              class="mt-4 md:mt-0 md:mr-4"
              @click="close"
            >
              Cancel
            </BaseButton>

            <div
              v-if="$store.state.requestToBookPath"
              class="flex justify-center items-center mt-2 text-grey-600 text-xs"
            >
              You won't be charged yet
            </div>

            <FinishEnquiryButton
              data-test-id="enquiry-form__mobile-submit-btn"
              @click="submit"
            />
          </div>

          <LegalNotice v-if="!$store.getters.isRtbFlowPossible && !isLoggedIn" is-account-created-on-this-view class="mb-6 leading-5" />
        </div>

        <ReviewsStars v-if="!$store.getters.isRtbFlowPossible" @track="track('tagvenue_reviews.io_widget', 'click')" />
      </div>
    </div>
  </BaseModal>
  <Captcha :config="$store.state.captcha" @onTokenResponse="(token) => { $store.commit('setCaptchaToken', token); }" />
</template>

<script>
import { mapState } from 'vuex';

import ReviewsStars from '@/components/ReviewsStars.vue';
import ScreenWidth from '@/components/ScreenWidth';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import RestrictionsInfo from '@/components/Ui/RestrictionsInfo.vue';

import AlreadyEnquired from './AlreadyEnquired.vue';
import BackButton from './EnquiryForm/components/buttons/BackButton.vue';
import FinishEnquiryButton from './EnquiryForm/components/buttons/FinishEnquiryButton.vue';
import CapacityExceededWarning from './EnquiryForm/components/CapacityExceeded/CapacityExceededWarning.vue';
import Captcha from './EnquiryForm/components/Captcha.vue';
import LegalNotice from './EnquiryForm/components/LegalNotice.vue';
import DateAndPeople from './EnquiryForm/forms/DateAndPeople.vue';
import MessageAndCatering from './EnquiryForm/forms/MessageAndCatering.vue';
import UserInfo from './EnquiryForm/forms/UserInfo.vue';
import EnquiryFormHeader from './EnquiryFormHeader/EnquiryFormHeader.vue';
import EnquiryPricingTable from './EnquiryPricingTable.vue';
import HasLeadHubOffer from './HasLeadHubOffer.vue';
import RtbRoomPricingBar from './RtbRoomPricingBar.vue';
import SuspensionWarning from './SuspensionWarning.vue';

export default {
  name: 'EnquiryFormModal',
  emits: ['close'],
  mixins: [ScreenWidth],
  components: {
    HasLeadHubOffer,
    RtbRoomPricingBar,
    BackButton,
    BaseButton,
    EnquiryPricingTable,
    SuspensionWarning,
    CapacityExceededWarning,
    AlreadyEnquired,
    Captcha,
    LegalNotice,
    FinishEnquiryButton,
    UserInfo,
    MessageAndCatering,
    DateAndPeople,
    ReviewsStars,
    EnquiryFormHeader,
    BaseModal,
    RestrictionsInfo,
  },
  data() {
    return {
      canTriggerOAuth: false,
      registerOrLoginForm: null,
      capacityExceededWarningConfig: {
        show: false,
        people: null,
      },
      isLoggingInProgress: false,
      hasUserInteracted: false,
    };
  },
  computed: {
    ...mapState([
      'room',
      'isLoading',
      'companyId',
    ]),
    shouldShowRestrictions() {
      const isTargetCompany = this.companyId === 2798;
      const hasRestrictions = this.restrictions.length > 0;
      return isTargetCompany && hasRestrictions && this.hasUserInteracted;
    },
    restrictions() {
      return [
        'Promoted / ticketed events are not allowed',
        'Guests under 18 are not allowed after 20:00',
      ];
    },
    form() { // For tracking only
      return {
        values: {
          ...this.$refs.dateAndPeople?.values,
          ...this.$refs.messageAndCatering?.values,
          ...this.$refs.userInfo?.values,
        },
        errors: {
          ...this.$refs.dateAndPeople?.errors,
          ...this.$refs.messageAndCatering?.errors,
          ...this.$refs.userInfo?.errors,
        },
      };
    },

    isLoggedIn() {
      return this.$store.state.user.isLoggedIn;
    },
    modalTitle() {
      if (!this.$store.getters.isRtbFlowPossible) {
        return 'Enquire now';
      }
      if (this.$store.state.requestToBookPath) {
        return 'Request to book';
      }
      return 'Message host';
    },
    modalSize() {
      if (!this.$store.state.requestToBookPath) {
        return 'large';
      }

      return 'default';
    },
  },

  watch: {
    registerOrLoginForm(value) {
      this.clearFormErrors();

      if (value === 'register') {
        this.$store.dispatch('trackGA4MessageHostInteraction', {
          section: 'signup_step',
          element: 'step',
          interaction: 'visible',
        });
      }
    },
  },

  methods: {
    onFormInteraction() {
      if (!this.hasUserInteracted) {
        this.hasUserInteracted = true;
      }
    },
    onSubmitMessageHost() {
      const { isValid } = this.$refs.userInfo.submit();
      if (!isValid) {
        return;
      }

      this.$store.dispatch('trackGA4MessageHostInteraction', {
        section: 'signup_step',
        element: 'send_message_button',
      });

      this.sendEnquiry();
    },
    continueAnyway() {
      if (this.$store.getters.isRtbFlowPossible) {
        if (this.$store.state.requestToBookPath && !this.$store.state.requestToBookMobilePath) {
          this.$store.dispatch('requestToBookFlow');
          return;
        }
        if (!this.isLoggedIn) {
          this.registerOrLoginForm = 'register';
          return;
        }
      }
      this.sendEnquiry();
    },
    onSuccessfulLoginOrRegister() {
      if (typeof window.__TGV_USER_DATA_LOAD === 'function') {
        window.__TGV_USER_DATA_LOAD();
      }

      if (this.registerOrLoginForm === 'register') {
        this.$store.commit('setUserSuccessfullySignUp');
      } else {
        this.$store.commit('setUserSuccessfullyLoggedIn');
      }

      this.sendEnquiry();
    },
    register() {
      const self = this;

      this.$store.dispatch('trackGA4MessageHostInteraction', {
        section: 'signup_step',
        element: 'send_message_button',
      });

      const { isValid } = this.$refs.userInfo.submit();
      if (!isValid) {
        return;
      }

      this.isLoggingInProgress = true;
      $.ajax({
        url: '/signup/client-without-password',
        type: 'POST',
        data: {
          _token: this.$store.state.api.token,
          fullname: this.form.values.fullName,
          email: this.form.values.email,
          phone: this.form.values.phone,
        },
        success(data, type, xhr) {
          if (xhr.status === 200) {
            self.onSuccessfulLoginOrRegister();
          } else {
            self.isLoggingInProgress = false;
            self.saveBackendErrors(data); // formValidation mixin
          }
        },
        error(response) {
          self.isLoggingInProgress = false;
          if (response.status === 409) {
            self.sendEnquiry();
          } else {
            self.$refs.userInfo.saveBackendErrors(response.responseJSON); // formValidation mixin
          }
        },
      });
    },
    login() {
      const self = this;
      const { isValid } = this.$refs.userInfo.submit();
      if (!isValid) {
        return;
      }

      this.isLoggingInProgress = true;
      $.ajax({
        url: '/login/client',
        type: 'POST',
        data: {
          'g-recaptcha-response': this.$store.state.captcha.token,
          _token: this.$store.state.api.token,
          email: this.form.values.email,
          password: this.form.values.password,
        },
        success(data, type, xhr) {
          if (xhr.status === 200) {
            self.onSuccessfulLoginOrRegister();
          } else {
            self.isLoggingInProgress = false;
            self.saveBackendErrors(data); // formValidation mixin
          }
        },
        error(response) {
          self.isLoggingInProgress = false;
          self.$refs.userInfo.saveBackendErrors(response.responseJSON); // formValidation mixin

          if (response.responseJSON?.['g-recaptcha-response']) {
            self.isLoggingInProgress = true;
            self.$store.commit('setCaptchaToken', null);
            self.$store.commit('enableCaptcha');
            console.log('Captcha requested');
            Rollbar.debug('Captcha requested');

            const unwatch = self.$watch('$store.state.captcha.token', () => {
              self.$store.commit('disableCaptcha');

              self.login();

              unwatch();
            });
          }
        },
      });
    },
    validateEntireForm() {
      const { isValid: isDateAndPeopleValid } = this.$refs.dateAndPeople.submit();
      const { isValid: isMessageAndCateringValid } = this.$refs.messageAndCatering.submit();
      this.canTriggerOAuth = isDateAndPeopleValid && isMessageAndCateringValid;
      this.scrollToError({ isDateAndPeopleValid, isMessageAndCateringValid });
    },

    clearFormErrors() {
      this.$refs.userInfo?.clearErrors();
    },

    submit(isOAuthSubmit) {
      let section = 'signup';
      let element = 'send_message_button';

      if (this.$store.state.isEnquiryFormModalOpen) {
        if (this.$store.getters.cta.thirdStep === 'Review request') {
          section = 'event_info';
          element = 'request_to_book_button';
        }
      }

      if (this.modalTitle === 'Message host') {
        this.$store.dispatch('trackGA4MessageHostInteraction', {
          section: 'event_info_step',
          element: this.isLoggedIn ? 'send_message_button' : 'continue_button',
        });
      } else {
        this.$store.dispatch('trackGA4Interaction', {
          section,
          element,
          value: null,
          interaction: 'click',
        });
      }

      const { isValid: isDateAndPeopleValid, data: dateAndPeopleForm } = this.$refs.dateAndPeople.submit();
      const { isValid: isMessageAndCateringValid } = this.$refs.messageAndCatering.submit();

      let isUserInfoValid = isOAuthSubmit || this.isLoggedIn || this.$refs.userInfo?.submit()?.isValid;
      if (!isUserInfoValid && this.$store.getters.isRtbFlowPossible) {
        isUserInfoValid = true;
      }
      const isValid = isDateAndPeopleValid && isMessageAndCateringValid && isUserInfoValid;
      if (!isValid) {
        this.scrollToError({ isDateAndPeopleValid, isMessageAndCateringValid, isUserInfoValid });
        return;
      }

      const people = parseInt(dateAndPeopleForm.people.value, 10);
      const isOverPeopleLimit = people > this.room.capacity.max;
      if (isOverPeopleLimit) {
        this.capacityExceededWarningConfig = {
          show: true,
          people,
        };
        return;
      }

      if (this.$store.getters.isRtbFlowPossible && this.$store.state.requestToBookPath && this.$store.state.requestToBookMobilePath && !this.isLoggedIn) {
        this.$store.dispatch('requestToBookFlow');
        return;
      }

      if (this.$store.getters.isRtbFlowPossible && !this.$store.state.requestToBookPath && !this.isLoggedIn) {
        this.registerOrLoginForm = 'register';
        return;
      }

      this.sendEnquiry();
    },

    sendEnquiry() {
      this.$store.dispatch('sendEnquiry');
      this.$store.dispatch('trackEnquiry', 'form');
    },

    scrollToError({ isDateAndPeopleValid, isMessageAndCateringValid, isUserInfoValid = true }) {
      if (!isDateAndPeopleValid && this.$refs.enquiryFormHeader?.$el) {
        this.$refs.enquiryFormHeader.$el.scrollIntoView({
          behavior: 'smooth',
        });
      } else if (!isMessageAndCateringValid) {
        this.$refs.messageAndCatering.$el.scrollIntoView({
          behavior: 'smooth',
        });
      } else if (isUserInfoValid && this.$refs.userInfo?.$el) {
        this.$refs.userInfo.$el.scrollIntoView({
          behavior: 'smooth',
        });
      }
    },

    close() {
      this.$emit('close');

      this.$store.dispatch('trackEnquiryFormNavigation', {
        label: 'mobile-close',
        values: this.form.values,
        errors: this.form.errors,
      });

      if (this.$store.getters.isRtbFlowPossible && !this.$store.state.requestToBookPath) {
        if (this.registerOrLoginForm === 'register') {
          this.$store.dispatch('trackGA4MessageHostInteraction', {
            section: 'signup_step',
            element: 'close_button',
          });
        } else {
          this.$store.dispatch('trackGA4MessageHostInteraction', {
            section: 'event_info_step',
            element: 'cancel_or_close_button',
          });
        }
      }
    },
    backButton() {
      this.$store.dispatch('trackGA4MessageHostInteraction', {
        section: 'signup_step',
        element: 'back_button',
      });

      this.registerOrLoginForm = null;
    },
    track(element, interaction, value = null) {
      let actionName = 'trackGA4Interaction';
      let section = 'event_info';

      if (this.$store.getters.isRtbFlowPossible && !this.$store.state.requestToBookPath) {
        // message to host modal
        actionName = 'trackGA4MessageHostInteraction';
        if (this.registerOrLoginForm === 'register') {
          section = 'signup_step';
        } else {
          section = 'event_info_step';
        }
      }

      this.$store.dispatch(actionName, {
        section,
        element,
        interaction,
        value,
      });
    },
  },
  mounted() {
    if (this.$store.getters.isRtbFlowPossible && !this.$store.state.requestToBookPath) {
      this.$store.dispatch('trackGA4MessageHostInteraction', {
        section: 'event_info_step',
        element: 'step',
        interaction: 'visible',
      });
    }
  },
};
</script>

<style lang="scss">
@use "@sass/base/variables";

.pika-single {
  z-index: 10050;
}
.enquiryFormModalSidebar {
  @media #{variables.$for-medium-and-up} {
    min-width: 300px;
  }
}
</style>
